import { useState, useEffect } from 'react'

export interface MediaDeviceInfo {
  deviceId: string
  label: string
  kind: MediaDeviceKind
}

export function PlaygroundDeviceSelector({
  kind,
  onDeviceChange,
  disabled,
}: {
  kind: MediaDeviceKind
  onDeviceChange: (deviceId: string) => void
  disabled?: boolean
}) {
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([])
  const [selectedDevice, setSelectedDevice] = useState<string>('')

  useEffect(() => {
    const loadDevices = async () => {
      try {
        const deviceList = await navigator.mediaDevices.enumerateDevices()
        const filteredDevices = deviceList
          .filter(device => device.kind === kind)
          .map(device => ({
            deviceId: device.deviceId,
            label: device.label || `${kind} ${device.deviceId.slice(0, 5)}`,
            kind: device.kind,
          }))
        setDevices(filteredDevices)

        // Set default device
        if (filteredDevices.length > 0 && !selectedDevice) {
          const defaultDevice = filteredDevices[0].deviceId
          setSelectedDevice(defaultDevice)
          onDeviceChange(defaultDevice)
        }
      } catch (error) {
        console.error('Error loading devices:', error)
      }
    }

    loadDevices()

    // Listen for device changes
    navigator.mediaDevices.addEventListener('devicechange', loadDevices)
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', loadDevices)
    }
  }, [kind, selectedDevice, onDeviceChange])

  const handleChange = (deviceId: string) => {
    setSelectedDevice(deviceId)
    onDeviceChange(deviceId)
  }

  const getKindLabel = () => {
    switch (kind) {
      case 'audioinput':
        return 'Microphone'
      case 'videoinput':
        return 'Camera'
      case 'audiooutput':
        return 'Speaker'
      default:
        return kind
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <label className="text-sm text-gray-400">{getKindLabel()}</label>
      <select
        value={selectedDevice}
        onChange={e => handleChange(e.target.value)}
        disabled={disabled || devices.length === 0}
        className="bg-gray-800 text-white rounded px-3 py-2 outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
      >
        {devices.map(device => (
          <option key={device.deviceId} value={device.deviceId}>
            {device.label}
          </option>
        ))}
      </select>
    </div>
  )
}
